import { Shield, ShieldCheck, Award, ArrowRight, Download, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { companyInfo, heroStats, certifications, CATALOGUE_URL } from '../../data/mock';
import { useInView } from '../../hooks/useInView';

export const HeroSection = () => {
  const [ref, isInView] = useInView();

  const scrollToInquiry = () => {
    const el = document.getElementById('inquiry');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative bg-navy-950 overflow-hidden" ref={ref}>
      {/* Background pattern */}
      <div className="absolute inset-0 grid-pattern opacity-60" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-ind-red-600/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-navy-700/20 rounded-full blur-[100px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 lg:pt-24 lg:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left content */}
          <div className={`${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
            {/* Certification badges */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <span className="inline-flex items-center gap-1.5 bg-ind-red-600/10 border border-ind-red-600/20 text-ind-red-400 text-xs font-semibold px-3 py-1.5 rounded-full">
                <ShieldCheck className="w-3.5 h-3.5" />
                ISO 9001:2015
              </span>
              <span className="inline-flex items-center gap-1.5 bg-navy-800/60 border border-navy-700 text-navy-200 text-xs font-semibold px-3 py-1.5 rounded-full">
                <Award className="w-3.5 h-3.5" />
                BIS Approved
              </span>
              <span className="inline-flex items-center gap-1.5 bg-navy-800/60 border border-navy-700 text-navy-200 text-xs font-semibold px-3 py-1.5 rounded-full">
                <Shield className="w-3.5 h-3.5" />
                ERDA | CPRI | NTH Tested
              </span>
            </div>

            <h1 className="font-heading font-extrabold text-white text-3xl sm:text-4xl lg:text-[52px] lg:leading-[1.1] mb-5">
              ISI Certified Industrial Hose &{' '}
              <span className="text-ind-red-400">Electrical Safety Solutions</span>
            </h1>

            <p className="text-navy-300 text-base lg:text-lg leading-relaxed mb-6 max-w-xl">
              Trusted by India's leading power plants, steel plants & infrastructure companies since 1984. Over four decades of manufacturing excellence.
            </p>

            {/* Key highlights */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {['ERDA, CPRI & NTH Tested Products', '40+ Years of Manufacturing', 'BIS Approved Products', 'Trusted Across India | Exporting Worldwide'].map((item) => (
                <div key={item} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-ind-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-navy-200">{item}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={scrollToInquiry}
                className="bg-ind-red-600 hover:bg-ind-red-700 text-white font-bold px-8 h-12 rounded-lg text-base shadow-lg shadow-ind-red-600/25 hover:shadow-ind-red-600/40"
              >
                Request a Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <a href={CATALOGUE_URL} target="_blank" rel="noopener noreferrer">
                <Button
                  variant="outline"
                  className="border-navy-600 text-navy-200 hover:bg-navy-800 hover:text-white h-12 rounded-lg text-base px-6"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Product Catalogue
                </Button>
              </a>
            </div>
          </div>

          {/* Right side - Stats + Visual */}
          <div className={`${isInView ? 'animate-slide-in-right' : 'opacity-0'}`} style={{ animationDelay: '0.3s' }}>
            <div className="bg-navy-900/60 backdrop-blur-sm border border-navy-800 rounded-2xl p-8 lg:p-10">
              <div className="grid grid-cols-2 gap-6">
                {heroStats.map((stat, idx) => (
                  <div key={idx} className="text-center p-4 bg-navy-800/40 rounded-xl border border-navy-700/50">
                    <p className="font-heading font-extrabold text-3xl lg:text-4xl text-white mb-1">{stat.value}</p>
                    <p className="text-sm text-navy-400 font-medium">{stat.label}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-navy-800">
                <p className="text-xs text-navy-400 mb-3 font-semibold uppercase tracking-wider">Certified & Tested By</p>
                <div className="flex flex-wrap items-center gap-3">
                  {certifications.map((cert) => (
                    <div
                      key={cert.name}
                      className="cert-badge bg-navy-800 border border-navy-700 rounded-lg px-4 py-2 text-center"
                    >
                      <p className="text-xs font-bold text-ind-red-400">{cert.name}</p>
                      <p className="text-[10px] text-navy-500">{cert.description.split(' ').slice(0, 2).join(' ')}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom wave/diagonal */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[hsl(210,20%,98%)] to-transparent" />
    </section>
  );
};
